/**
 * Which of the user's tenants each dashboard widget can offer. Mirrors
 * `ProtectedModuleRoute`; this is UX only, as the server enforces access.
 */
import { includesIgnoreCase } from "../utils";

export type DashboardModule = "core" | "reporting" | "lessons-learned";

export interface DashboardTenant {
	key: string;
	name: string;
	isMaster: boolean;
}

const MASTER_TENANT_KEY = import.meta.env.VITE_MASTER_TENANT_KEY;

/** Modules the client gates per user, through `modules_authorized_users`. */
const USER_GATED_MODULES = new Set<DashboardModule>([
	"reporting",
	"lessons-learned",
]);

export const toDashboardTenant = (tenant: any): DashboardTenant => ({
	key: tenant.role_key,
	name: tenant.name || tenant.displayName || tenant.role_key,
	isMaster: !!MASTER_TENANT_KEY && tenant.role_key === MASTER_TENANT_KEY,
});

const canUseModule = (
	tenant: any,
	module: DashboardModule,
	email: string | undefined,
	isSuperAdmin: boolean
): boolean => {
	if (!tenant?.role_key) return false;
	if (
		!Array.isArray(tenant.subscriptions) ||
		!tenant.subscriptions.includes(module)
	) {
		return false;
	}
	if (isSuperAdmin || !USER_GATED_MODULES.has(module)) return true;
	if (!includesIgnoreCase(tenant.modules_authorized_users?.[module], email)) {
		return false;
	}
	return (
		module !== "reporting" ||
		(Array.isArray(tenant.enabled_reporting_types) &&
			tenant.enabled_reporting_types.length > 0)
	);
};

/** The tenants, in `tenantList` order, where the user can use `module`. */
export const tenantsWithModule = (
	tenantList: any[],
	module: DashboardModule,
	email: string | undefined,
	isSuperAdmin: boolean
): DashboardTenant[] =>
	tenantList
		.filter((tenant) => canUseModule(tenant, module, email, isSuperAdmin))
		.map(toDashboardTenant);
